import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatCurrency, formatDateRu, parseBackendDate } from '../utils/formatUtils';
import { 
  ArrowLeft, 
  ChevronLeft,
  ChevronRight,
  Edit3, 
  Save, 
  X, 
  MessageSquare, 
  Send,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  User,
  Activity,
  Trash2,
  AlertTriangle,
  Bell,
  Plus,
  Clock,
  Check,
  TrendingUp,
  BarChart3,
  Eye,
  EyeOff,
  Key,
  Copy
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientCard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t, tt } = useLanguage();
  const { formatDateTime, formatDate, formatTime } = useTimezone();
  const [client, setClient] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [managers, setManagers] = useState([]);
  const [editForm, setEditForm] = useState({});
  
  // Navigation state for prev/next client
  const [clientsList, setClientsList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [newDeposit, setNewDeposit] = useState({
    amount: '',
    notes: ''
  });
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [passwordChangeForm, setPasswordChangeForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [deposits, setDeposits] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showEditDepositModal, setShowEditDepositModal] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [editingDepositForm, setEditingDepositForm] = useState({
    amount: '',
    notes: ''
  });
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: 'Звонок',
    description: '',
    scheduled_time: ''
  });
  const [showEditReminderModal, setShowEditReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [editingReminderForm, setEditingReminderForm] = useState({
    title: '',
    description: '',
    scheduled_time: ''
  });
  const [showDeleteDepositConfirm, setShowDeleteDepositConfirm] = useState(false);
  const [depositToDelete, setDepositToDelete] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });

  const statuses = [
    { value: 'new', label: tt('Новый') },
    { value: 'ftd', label: tt('ФТД') },
    { value: 'in_progress', label: tt('В работе') },
    { value: 'potential', label: tt('Потенциальный') },
    { value: 'not_potential', label: tt('Не потенциальный') },
    { value: 'under_18', label: tt('Нет 18') },
    { value: 'no_russian', label: tt('Не говорит на рус') },
    { value: 'not_interested', label: tt('Не интересно') },
    { value: 'no_answer', label: tt('Не отвечает') },
    { value: 'replace', label: tt('Заменить') },
    { value: 'duplicate', label: tt('Дубль') },
    { value: 'fake', label: tt('Фейк') }
  ];

  useEffect(() => {
    fetchClient();
    fetchComments();
    fetchDeposits();
    fetchApplications();
    fetchManagers();
    fetchReminders();
    
    // Load clients list from sessionStorage for navigation
    const savedClients = sessionStorage.getItem('clientsList');
    if (savedClients) {
      try {
        const clients = JSON.parse(savedClients);
        setClientsList(clients);
        const index = clients.findIndex(c => String(c.id) === String(id));
        setCurrentIndex(index);
      } catch (e) {
        console.error('Error loading clients list:', e);
      }
    }
  }, [id]);

  // Initialize edit form when client loads
  useEffect(() => {
    if (client) {
      setEditForm({
        name: client.name,
        email: client.email,
        phone: client.phone,
        country: client.country,
        campaign: client.campaign,
        source: client.source,
        status: client.status,
        assigned_manager: client.assigned_manager,
        notes: client.notes || ''
      });
    }
  }, [client]);
  
  // Navigation functions
  const goToPreviousClient = () => {
    if (currentIndex > 0 && clientsList.length > 0) {
      const prevClient = clientsList[currentIndex - 1];
      navigate(`/crm/clients/${prevClient.id}`);
    }
  };
  
  const goToNextClient = () => {
    if (currentIndex < clientsList.length - 1 && clientsList.length > 0) {
      const nextClient = clientsList[currentIndex + 1];
      navigate(`/crm/clients/${nextClient.id}`);
    }
  };

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
      console.log('🧑‍💼 Client data loaded:', response.data);
    } catch (error) {
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${id}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setComments(response.data);
      console.log('📝 Комментарии загружены:', response.data.length);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchDeposits = async () => {
    try {
      const response = await axios.get(`${API}/deposits?client_id=${id}`);
      console.log('💰 Депозиты загружены для клиента', id, ':', response.data);
      setDeposits(response.data);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/withdraw-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Filter applications for this specific client
      const clientApplications = response.data.filter(app => 
        app.client_id === id || app.client_id === parseInt(id)
      );
      setApplications(clientApplications);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
    }
  };

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Staff endpoint already returns only managers, no need to filter
      setManagers(response.data);
      console.log('👥 Менеджеры загружены:', response.data.length);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}/reminders`);
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${id}`, editForm, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setClient({ ...client, ...editForm });
      setEditing(false);
      console.log('✅ Client updated successfully');
    } catch (error) {
      console.error('Error updating client:', error);
      
      let errorMessage = 'Ошибка при обновлении клиента';
      
      if (error.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail) 
          ? error.response.data.detail.map(d => typeof d === 'string' ? d : d.msg || JSON.stringify(d)).join(', ')
          : typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
      }
      
      alert(`Ошибка: ${errorMessage}`);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || savingComment) return;

    setSavingComment(true);
    setCommentSuccess(false);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/clients/${id}/comments`, {
        client_id: parseInt(id),
        text: newComment,
        is_pinned: isPinned
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setNewComment('');
      setIsPinned(false);
      setCommentSuccess(true);
      setTimeout(() => setCommentSuccess(false), 3000);
      fetchComments();
      fetchClient(); // Update client data to reflect last_comment_date
      console.log('Комментарий добавлен успешно');
    } catch (error) {
      console.error('Error adding comment:', error);
      let errorMessage = 'Неизвестная ошибка';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(d => typeof d === 'string' ? d : d.msg || JSON.stringify(d)).join(', ')
            : typeof error.response.data.detail === 'string' 
              ? error.response.data.detail 
              : JSON.stringify(error.response.data.detail);
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('Ошибка при добавлении комментария: ' + errorMessage);
    } finally {
      setSavingComment(false);
    }
  };

  // [COMMENTS-DELETE-2026-07-17] Superadmin-only delete of a single comment.
  const handleDeleteComment = async (commentId) => {
    if (user?.role !== 'superadmin') return;
    if (!window.confirm(tt('Удалить этот комментарий? Это действие нельзя отменить.'))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clients/${id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchComments();
    } catch (error) {
      alert(tt('Не удалось удалить комментарий') + ': ' + (error?.response?.data?.detail || error.message || ''));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword) {
      alert(tt('Пароли не совпадают'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${id}/change-password`, {
        new_password: passwordChangeForm.newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      alert(tt('Пароль успешно изменен'));
      setShowPasswordChangeModal(false);
      setPasswordChangeForm({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      let errorMessage = 'Ошибка при смене пароля';
      
      if (error.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail) 
          ? error.response.data.detail.map(d => typeof d === 'string' ? d : d.msg || JSON.stringify(d)).join(', ')
          : typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
      }
      
      alert(`Ошибка: ${errorMessage}`);
    }
  };

  const handleRegisterUser = async (e) => {
    e.preventDefault();
    if (!registrationForm.name || !registrationForm.email || !registrationForm.password) {
      alert(tt('Заполните все обязательные поля'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Try to register new user
      const response = await axios.post(`${API}/auth/register`, {
        ...registrationForm,
        role: 'client'
      });
      
      // Link client to user
      await axios.put(`${API}/clients/${id}`, {
        user_id: response.data.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      alert('Клиент успешно зарегистрирован!\n\n✅ Создан пользователь на торговой платформе\n✅ Создан торговый аккаунт в CRM\n\nКлиент теперь может войти на торговую платформу.');
      setShowRegistrationModal(false);
      setRegistrationForm({
        name: '',
        email: '',
        phone: '',
        password: ''
      });
      
      await fetchClient();
      
    } catch (error) {
      console.error('Error registering client:', error);
      
      // Check if error is "Email already registered"
      if (error.response?.data?.detail === 'Email already registered') {
        // User already exists, just create trading balance
        try {
          const token = localStorage.getItem('token');
          
          // Set initial trading balance (creates trading account)
          await axios.put(`${API}/trading/admin/balance/${id}`, {
            balance: 0
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          alert('✅ Торговый счет создан!\n\nПользователь с этим email уже зарегистрирован. Торговый счет успешно создан и привязан к клиенту.\n\nКлиент может войти на торговую платформу используя существующий пароль.');
          setShowRegistrationModal(false);
          setRegistrationForm({
            name: '',
            email: '',
            phone: '',
            password: ''
          });
          
          await fetchClient();
          return;
        } catch (balanceError) {
          console.error('Error creating trading balance:', balanceError);
          alert(tt('Ошибка: Пользователь существует, но не удалось создать торговый счет. Обратитесь к администратору.'));
          return;
        }
      }
      
      // Other errors
      let errorMessage = 'Ошибка при регистрации клиента';
      
      if (error.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail) 
          ? error.response.data.detail.map(d => typeof d === 'string' ? d : d.msg || JSON.stringify(d)).join(', ')
          : typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
      }
      
      alert(`Ошибка: ${errorMessage}`);
    }
  };

  const handleAddDeposit = async (e) => {
    e.preventDefault();
    if (!newDeposit.amount || parseFloat(newDeposit.amount) <= 0) return;

    try {
      await axios.post(`${API}/deposits`, {
        client_id: id,
        amount: parseFloat(newDeposit.amount),
        notes: newDeposit.notes
      });
      setNewDeposit({ amount: '', notes: '' });
      setShowDepositModal(false);
      fetchClient();
      fetchDeposits();
    } catch (error) {
      console.error('Error adding deposit:', error);
    }
  };

  const handleEditDeposit = (deposit) => {
    setEditingDeposit(deposit);
    setEditingDepositForm({
      amount: deposit.amount.toString(),
      notes: deposit.notes || ''
    });
    setShowEditDepositModal(true);
  };

  const handleUpdateDeposit = async (e) => {
    e.preventDefault();
    if (!editingDeposit) return;

    try {
      await axios.put(`${API}/deposits/${editingDeposit.id}`, {
        amount: parseFloat(editingDepositForm.amount),
        notes: editingDepositForm.notes
      });
      
      setShowEditDepositModal(false);
      setEditingDeposit(null);
      setEditingDepositForm({ amount: '', notes: '' });
      fetchDeposits();
      fetchClient();
    } catch (error) {
      console.error('Error updating deposit:', error);
    }
  };

  const handleDeleteDeposit = async (depositId) => {
    try {
      await axios.delete(`${API}/deposits/${depositId}`);
      fetchDeposits();
      fetchClient();
      setShowDeleteDepositConfirm(false);
      setDepositToDelete(null);
    } catch (error) {
      console.error('Error deleting deposit:', error);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.title || !newReminder.scheduled_time) return;

    try {
      // [NOTIF-2026-05-21] Multi-country friendly: каждый сотрудник вводит время
      // по СВОИМ часам (browser local). `new Date(<input>).toISOString()` берёт
      // local clock-time, конвертит в UTC и шлёт на бэк → бэк хранит абсолютный
      // момент, бэллик зазвонит когда сервер UTC матчит — а сотрудник увидит
      // это в своём часовом поясе.
      const payload = {
        ...newReminder,
        scheduled_time: new Date(newReminder.scheduled_time).toISOString(),
      };
      await axios.post(`${API}/clients/${id}/reminders`, payload);
      setNewReminder({
        title: 'Звонок',
        description: '',
        scheduled_time: ''
      });
      setShowReminderModal(false);
      fetchReminders();
    } catch (error) {
      console.error('Error adding reminder:', error);
    }
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    // [NOTIF-2026-05-22] Конвертим UTC ISO → browser local "YYYY-MM-DDTHH:MM"
    // через parseBackendDate чтобы legacy naive-ISO не интерпретировались как
    // local (даёт сдвиг на browser-tz). Сотрудник видит время по своим часам.
    const d = parseBackendDate(reminder.scheduled_time);
    const pad = (n) => String(n).padStart(2, '0');
    const localISO = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setEditingReminderForm({
      title: reminder.title,
      description: reminder.description || '',
      scheduled_time: localISO,
    });
    setShowEditReminderModal(true);
  };

  const handleUpdateReminder = async (e) => {
    e.preventDefault();
    if (!editingReminder) return;

    try {
      // [NOTIF-2026-05-21] Same browser-local → UTC convention as add.
      const payload = {
        ...editingReminderForm,
        scheduled_time: new Date(editingReminderForm.scheduled_time).toISOString(),
      };
      await axios.put(`${API}/reminders/${editingReminder.id}`, payload);
      setShowEditReminderModal(false);
      setEditingReminder(null);
      setEditingReminderForm({
        title: '',
        description: '',
        scheduled_time: ''
      });
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const updateReminderStatus = async (reminderId, status) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}`, { status });
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder status:', error);
    }
  };

  const deleteReminder = async (reminderId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это напоминание?')) return;

    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      fetchReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const findTradingAccount = async () => {
    try {
      const response = await axios.get(`${API}/trading/admin/accounts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const account = response.data.accounts?.find(acc => 
        acc.client_id == id || acc.client_id == parseInt(id)
      );
      
      if (account) {
        navigate(`/crm/trading-accounts/${account.id}`);
      } else {
        alert(tt('Торговый аккаунт для этого клиента не найден'));
      }
    } catch (error) {
      console.error('Error finding trading account:', error);
      // Fallback to general trading accounts page
      navigate('/crm/trading-accounts');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="spinner mr-3"></div>
          <span className="text-gray-600">{tt('Загрузка клиента...')}</span>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">{tt('Клиент не найден')}</h3>
          <Link to="/crm/clients" className="btn btn-primary">
            {tt('Вернуться к списку клиентов')}
          </Link>
        </div>
      </div>
    );
  }

  // All CRM staff can edit (except clients)
  const canEdit = user?.role !== 'client';
  
  const getStatusDisplay = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'ftd': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'potential': return 'bg-purple-100 text-purple-800';
      case 'not_potential': return 'bg-red-100 text-red-800';
      case 'under_18': return 'bg-orange-100 text-orange-800';
      case 'no_russian': return 'bg-pink-100 text-pink-800';
      case 'not_interested': return 'bg-gray-100 text-gray-800';
      case 'no_answer': return 'bg-orange-100 text-orange-800';
      case 'replace': return 'bg-indigo-100 text-indigo-800';
      case 'duplicate': return 'bg-red-200 text-red-900';
      case 'fake': return 'bg-red-300 text-red-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getManagerName = (managerId) => {
    if (!managerId) return 'Не назначен';
    
    console.log('🔍 Поиск менеджера:', managerId, 'в списке:', managers.map(m => ({ id: m.id, name: m.name })));
    
    // Try both string and number comparison
    const manager = managers.find(m => 
      m.id == managerId || m.id === String(managerId) || m.id === parseInt(managerId)
    );
    return manager ? manager.name : 'Не назначен';
  };

  return (
    <div className="p-6">
      {/* Header with navigation and actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Link
            to="/crm/clients"
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          
          {/* Previous/Next Navigation */}
          {clientsList.length > 0 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPreviousClient}
                disabled={currentIndex <= 0}
                className={`p-2 rounded-lg transition-colors ${
                  currentIndex <= 0 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-blue-600 hover:bg-blue-50'
                }`}
                title={tt('Предыдущий клиент')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-500">
                {currentIndex + 1} / {clientsList.length}
              </span>
              <button
                onClick={goToNextClient}
                disabled={currentIndex >= clientsList.length - 1}
                className={`p-2 rounded-lg transition-colors ${
                  currentIndex >= clientsList.length - 1
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-blue-600 hover:bg-blue-50'
                }`}
                title={tt('Следующий клиент')}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          
          <div>
            <p className="text-gray-600">ID клиента: {client.id}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Link to Trading Account */}
          <button
            onClick={findTradingAccount}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <BarChart3 className="w-4 h-4" />
            <span>{tt('Торговый аккаунт')}</span>
          </button>

          {client.user_id && canEdit && (
            <button
              onClick={() => setShowPasswordChangeModal(true)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Key className="w-4 h-4" />
              <span>{tt('Сменить пароль')}</span>
            </button>
          )}
          
          {!client.user_id && canEdit && (
            <button
              onClick={() => {
                setShowRegistrationModal(true);
                // Заполняем форму данными клиента
                setRegistrationForm({
                  name: client.name || '',
                  email: client.email || '',
                  phone: client.phone || '',
                  password: ''
                });
              }}
              className="btn btn-primary flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>{tt('Зарегистрировать пользователя')}</span>
            </button>
          )}
          
          {canEdit && (
            editing ? (
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{tt('Сохранить')}</span>
                </button>
                <button
                  onClick={() => setShowPasswordChangeModal(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center space-x-2"
                >
                  <Key className="w-4 h-4" />
                  <span>{tt('Сменить пароль')}</span>
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="btn btn-secondary flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>{tt('Отмена')}</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <Edit3 className="w-4 h-4" />
                <span>{tt('Редактировать')}</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main client info */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Basic Information */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{tt('Основная информация')}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    {tt('Имя')}
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <p className="text-gray-900">{client.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </label>
                  {editing ? (
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <p className="text-gray-900">{client.email || 'Не указан'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    {tt('Телефон')}
                  </label>
                  {editing ? (
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900">{client.phone || 'Не указан'}</p>
                      {client.phone && (
                        <button
                          onClick={() => {
                            // Копируем только цифры (без +)
                            const digitsOnly = client.phone.replace(/\D/g, '');
                            navigator.clipboard.writeText(digitsOnly);
                            // Показываем уведомление
                            const btn = document.getElementById('phone-copy-btn');
                            if (btn) {
                              btn.classList.add('text-green-500');
                              setTimeout(() => btn.classList.remove('text-green-500'), 1000);
                            }
                          }}
                          id="phone-copy-btn"
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={tt('Копировать номер (только цифры)')}
                        >
                          <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    {tt('Страна')}
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <p className="text-gray-900">{client.country || 'Не указана'}</p>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Статус лида')}</label>
                  {editing ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="input"
                    >
                      {statuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(client.status)}`}>
                      {getStatusDisplay(client.status)}
                    </span>
                  )}
                </div>

                {/* Only Team Leads, Admins, and Superadmins can change assigned manager */}
                {(user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Менеджер')}</label>
                    {editing ? (
                      <select
                        value={editForm.assigned_manager || ''}
                        onChange={(e) => setEditForm({ ...editForm, assigned_manager: e.target.value })}
                        className="input"
                      >
                        <option value="">{tt('Не назначен')}</option>
                        {managers.map(manager => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name} ({manager.role})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-900">{getManagerName(client.assigned_manager)}</p>
                    )}
                  </div>
                )}
                
                {/* Regular managers see but cannot edit */}
                {!(user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Менеджер')}</label>
                    <p className="text-gray-900">{getManagerName(client.assigned_manager)}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Кампания')}</label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.campaign}
                      onChange={(e) => setEditForm({ ...editForm, campaign: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <p className="text-gray-900">{client.campaign || 'Не указана'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Источник')}</label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.source}
                      onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                      className="input"
                    />
                  ) : (
                    <p className="text-gray-900">{client.source || 'Не указан'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Заметки')}</label>
              {editing ? (
                <textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="input"
                  placeholder={tt('Добавьте заметки о клиенте...')}
                />
              ) : (
                <p className="text-gray-900">{client.notes || 'Заметки отсутствуют'}</p>
              )}
            </div>

            {/* Timestamps */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Создан: {formatDateTime(client.created_at)}
                </span>
                {client.updated_at && (
                  <span>
                    Обновлен: {formatDateTime(client.updated_at)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Комментарии ({comments.length})
              </h2>
            </div>

            {/* Add comment form */}
            {canEdit && (
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="w-full">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault();
                        if (newComment.trim() && !savingComment) {
                          handleAddComment(e);
                        }
                      }
                    }}
                    placeholder={tt('Добавить комментарий... (Ctrl+Enter для отправки)')}
                    rows={3}
                    className="input resize-none w-full"
                    disabled={savingComment}
                    data-testid="comment-textarea"
                  />
                  {commentSuccess && (
                    <div className="mt-2 text-sm text-green-600 font-medium flex items-center gap-1" data-testid="comment-success-msg">
                      <Check className="w-4 h-4" />
                      {tt('Комментарий сохранен')}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isPinned}
                        onChange={(e) => setIsPinned(e.target.checked)}
                        className="rounded border-gray-300"
                        disabled={savingComment}
                      />
                      <span className="text-sm text-gray-600">{tt('Закрепить комментарий')}</span>
                    </label>
                    <button
                      type="submit"
                      disabled={!newComment.trim() || savingComment}
                      className="btn btn-primary flex items-center space-x-2"
                      data-testid="submit-comment-btn"
                    >
                      {savingComment ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{tt('Сохранение...')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>{tt('Отправить')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Comments list */}
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className={`border rounded-lg p-4 ${
                  comment.is_pinned ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{comment.user_name}</span>
                      {comment.is_pinned && (
                        <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                          {tt('Закреплено')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{formatDateTime(comment.created_at)}</span>
                      {user?.role === 'superadmin' && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded"
                          title={tt('Удалить комментарий')}
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                </div>
              ))}

              {comments.length === 0 && (
                <p className="text-gray-500 text-center py-4">{tt('Комментарии отсутствуют')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Right column - Sidebar */}
        <div className="space-y-6">
          {/* Client stats */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Статистика клиента')}</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm text-gray-700">{tt('Сумма депозитов')}</span>
                </div>
                <span className="font-semibold text-green-600">${deposits.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <Activity className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm text-gray-700">{tt('Депозиты')}</span>
                </div>
                <div className="text-right">
                  <span className="font-semibold text-blue-600">{deposits.length}</span>
                  <div className="text-xs text-blue-500">
                    ${deposits.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center">
                  <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-700">{tt('Заявки на вывод')}</span>
                </div>
                <span className="font-semibold text-purple-600">{applications.length}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{tt('Первый депозит (FTD)')}</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  client.ftd ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {client.ftd ? 'Да' : 'Нет'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{tt('Пользователь зарегистрирован')}</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  client.user_id ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {client.user_id ? 'Да' : 'Нет'}
                </span>
              </div>
            </div>
          </div>

          {/* Reminders */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{tt('Напоминания')}</h3>
              {canEdit && (
                <button
                  onClick={() => setShowReminderModal(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{tt('Добавить')}</span>
                </button>
              )}
            </div>

            <div className="space-y-3">
              {reminders.slice(0, 5).map((reminder) => (
                <div key={reminder.id} className={`border rounded-lg p-3 ${
                  reminder.status === 'completed' ? 'bg-green-50 border-green-200' :
                  reminder.status === 'cancelled' ? 'bg-red-50 border-red-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{reminder.title}</span>
                    {canEdit && (
                      <div className="flex space-x-1">
                        {reminder.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateReminderStatus(reminder.id, 'completed')}
                              className="text-green-600 hover:text-green-800"
                              title={tt('Выполнено')}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => updateReminderStatus(reminder.id, 'cancelled')}
                              className="text-red-600 hover:text-red-800"
                              title={tt('Отменить')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEditReminder(reminder)}
                          className="text-blue-600 hover:text-blue-800"
                          title={tt('Редактировать')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteReminder(reminder.id)}
                          className="text-red-600 hover:text-red-800"
                          title={tt('Удалить')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {reminder.description && (
                    <p className="text-xs text-gray-600 mb-2">{reminder.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatDateTime(reminder.scheduled_time)}
                    </span>
                    <span className={`px-2 py-1 rounded ${
                      reminder.status === 'completed' ? 'bg-green-100 text-green-800' :
                      reminder.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {reminder.status === 'completed' ? 'Выполнено' :
                       reminder.status === 'cancelled' ? 'Отменено' : 'Ожидание'}
                    </span>
                  </div>
                </div>
              ))}

              {reminders.length === 0 && (
                <p className="text-gray-500 text-center py-4 text-sm">{tt('Напоминания отсутствуют')}</p>
              )}

              {reminders.length > 5 && (
                <Link to="/crm/calendar" className="text-blue-600 hover:text-blue-800 text-sm">
                  {tt('Показать все напоминания →')}
                </Link>
              )}
            </div>
          </div>

          {/* Deposits Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{tt('Депозиты')}</h3>
              {canEdit && (
                <button
                  onClick={() => setShowDepositModal(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{tt('Добавить')}</span>
                </button>
              )}
            </div>

            {deposits.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>{tt('Депозиты не найдены')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {deposits.slice(0, 5).map((deposit) => (
                  <div key={deposit.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-green-600">${deposit.amount.toFixed(2)}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        deposit.deposit_type === 'ftd' 
                          ? 'bg-green-100 text-green-800'
                          : deposit.deposit_type === 'reload'
                          ? 'bg-blue-100 text-blue-800'
                          : deposit.deposit_type === 'bonus'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {deposit.deposit_type === 'ftd' ? 'FTD' :
                         deposit.deposit_type === 'reload' ? 'Reload' :
                         deposit.deposit_type === 'bonus' ? 'Bonus' :
                         deposit.deposit_type || 'Deposit'}
                      </span>
                    </div>
                    {deposit.notes && (
                      <p className="text-xs text-gray-600 mb-1">{deposit.notes}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDateTime(deposit.created_at)}</span>
                      {canEdit && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditDeposit(deposit)}
                            className="text-blue-600 hover:text-blue-800"
                            title={tt('Редактировать')}
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => {
                              setDepositToDelete(deposit);
                              setShowDeleteDepositConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title={tt('Удалить')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {deposits.length > 5 && (
                  <p className="text-blue-600 hover:text-blue-800 text-sm text-center cursor-pointer">
                    Показать все ({deposits.length}) →
                  </p>
                )}

                <div className="pt-2 border-t mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Всего: {deposits.length}</span>
                  <span className="font-semibold text-green-600">
                    ${deposits.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Recent applications */}
          {applications.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Заявки на вывод')}</h3>
              
              <div className="space-y-3">
                {applications.slice(0, 3).map((app) => (
                  <div key={app.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">${app.amount.toFixed(2)}</span>
                      <span className={`px-2 py-1 text-xs rounded ${
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {app.status === 'approved' ? 'Одобрено' :
                         app.status === 'rejected' ? 'Отклонено' : 'На рассмотрении'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      {formatDateTime(app.created_at)}
                    </div>
                    
                    {app.card_number && (
                      <div className="text-xs text-gray-600 mt-1">
                        Карта: ****{app.card_number.slice(-4)}
                      </div>
                    )}
                  </div>
                ))}

                {applications.length > 3 && (
                  <Link to="/crm/applications" className="text-blue-600 hover:text-blue-800 text-sm">
                    {tt('Показать все заявки →')}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All the modals remain the same but I'll include them... */}
      
      {/* Add Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Добавить депозит')}</h3>
            
            <form onSubmit={handleAddDeposit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Сумма депозита')}
                  </label>
                  <input
                    type="number"
                    value={newDeposit.amount}
                    onChange={(e) => setNewDeposit({ ...newDeposit, amount: e.target.value })}
                    className="input"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Примечания')}
                  </label>
                  <input
                    type="text"
                    value={newDeposit.notes}
                    onChange={(e) => setNewDeposit({ ...newDeposit, notes: e.target.value })}
                    className="input"
                    placeholder={tt('Необязательно')}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepositModal(false);
                    setNewDeposit({ amount: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!newDeposit.amount || parseFloat(newDeposit.amount) <= 0}
                >
                  {tt('Добавить депозит')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Deposit Modal */}
      {showEditDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Редактировать депозит')}</h3>
            
            <form onSubmit={handleUpdateDeposit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Сумма депозита')}
                  </label>
                  <input
                    type="number"
                    value={editingDepositForm.amount}
                    onChange={(e) => setEditingDepositForm({ ...editingDepositForm, amount: e.target.value })}
                    className="input"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Примечания')}
                  </label>
                  <input
                    type="text"
                    value={editingDepositForm.notes}
                    onChange={(e) => setEditingDepositForm({ ...editingDepositForm, notes: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditDepositModal(false);
                    setEditingDeposit(null);
                    setEditingDepositForm({ amount: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {tt('Сохранить изменения')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Изменить пароль')}</h3>
            
            <form onSubmit={handleChangePassword}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Новый пароль')}
                  </label>
                  <input
                    type="password"
                    value={passwordChangeForm.newPassword}
                    onChange={(e) => setPasswordChangeForm({ 
                      ...passwordChangeForm, 
                      newPassword: e.target.value 
                    })}
                    className="input"
                    required
                    minLength="6"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Подтвердите пароль')}
                  </label>
                  <input
                    type="password"
                    value={passwordChangeForm.confirmPassword}
                    onChange={(e) => setPasswordChangeForm({ 
                      ...passwordChangeForm, 
                      confirmPassword: e.target.value 
                    })}
                    className="input"
                    required
                    minLength="6"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChangeModal(false);
                    setPasswordChangeForm({
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    !passwordChangeForm.newPassword || 
                    !passwordChangeForm.confirmPassword ||
                    passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword
                  }
                >
                  {tt('Изменить пароль')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Регистрация пользователя')}</h3>
            
            <form onSubmit={handleRegisterUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Имя *')}
                  </label>
                  <input
                    type="text"
                    value={registrationForm.name}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={registrationForm.email}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, email: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Телефон')}
                  </label>
                  <input
                    type="tel"
                    value={registrationForm.phone}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, phone: e.target.value })}
                    className="input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Пароль *')}
                  </label>
                  <input
                    type="password"
                    value={registrationForm.password}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, password: e.target.value })}
                    className="input"
                    required
                    minLength="6"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowRegistrationModal(false);
                    setRegistrationForm({ name: '', email: '', phone: '', password: '' });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {tt('Зарегистрировать')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Добавить напоминание')}</h3>
            
            <form onSubmit={handleAddReminder}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Заголовок')}
                  </label>
                  <input
                    type="text"
                    value={newReminder.title}
                    className="input bg-gray-100 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Описание (по желанию)')}
                  </label>
                  <textarea
                    value={newReminder.description}
                    onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                    className="input"
                    rows={3}
                    placeholder={tt('Дополнительная информация...')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Дата и время *')}
                  </label>
                  <input
                    type="datetime-local"
                    value={newReminder.scheduled_time}
                    onChange={(e) => setNewReminder({ ...newReminder, scheduled_time: e.target.value })}
                    className="input"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowReminderModal(false);
                    setNewReminder({
                      title: 'Звонок',
                      description: '',
                      scheduled_time: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {tt('Добавить напоминание')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Reminder Modal */}
      {showEditReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Редактировать напоминание')}</h3>
            
            <form onSubmit={handleUpdateReminder}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Заголовок *')}
                  </label>
                  <input
                    type="text"
                    value={editingReminderForm.title}
                    onChange={(e) => setEditingReminderForm({ 
                      ...editingReminderForm, 
                      title: e.target.value 
                    })}
                    className="input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Описание')}
                  </label>
                  <textarea
                    value={editingReminderForm.description}
                    onChange={(e) => setEditingReminderForm({ 
                      ...editingReminderForm, 
                      description: e.target.value 
                    })}
                    className="input"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tt('Дата и время *')}
                  </label>
                  <input
                    type="datetime-local"
                    value={editingReminderForm.scheduled_time}
                    onChange={(e) => setEditingReminderForm({ 
                      ...editingReminderForm, 
                      scheduled_time: e.target.value 
                    })}
                    className="input"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditReminderModal(false);
                    setEditingReminder(null);
                    setEditingReminderForm({
                      title: '',
                      description: '',
                      scheduled_time: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {tt('Сохранить изменения')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Deposit Confirmation */}
      {showDeleteDepositConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Подтверждение удаления')}</h3>
            <p className="text-gray-600 mb-6">
              Вы уверены, что хотите удалить депозит на сумму ${depositToDelete?.amount.toFixed(2)}?
              Это действие нельзя будет отменить.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDepositConfirm(false);
                  setDepositToDelete(null);
                }}
                className="btn btn-secondary"
              >
                {tt('Отмена')}
              </button>
              <button
                onClick={() => handleDeleteDeposit(depositToDelete.id)}
                className="btn btn-danger"
              >
                {tt('Удалить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCard;
